/**
 * @file index.tsx
 *
 * @fileoverview Represents a job detail page.
 */

import React, { Component } from 'react';
import { getJobByPath } from '~/jobiqo/components/Job/graphql/queries/jobQueries';
import { JobPageDetail } from '~/jobiqo/components/Job';
import { ApolloError } from '@apollo/client';
import { User } from '~/jobiqo/models/user';
import { Entity } from '~/jobiqo/models/entity';
import { Job } from '~/jobiqo/types/types';
import initApollo, { addApolloState } from '~/jobiqo/apollo/initApollo';
import { GetServerSideProps } from 'next';
import { commonQueries } from '~/jobiqo/lib/data/commonQueries';
import { getTranslatedLink } from '~/jobiqo/utils/links';
import { getCleanUrlPart } from '~/jobiqo/components/Search/utils/searchHelpers';
import JobAlertProvider from '~/jobiqo/components/JobAlert/context';
import { GdprConsentProvider } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import { gsspRedirect, isRedirectCode } from '~/jobiqo/lib/redirect';
import { getIsJobPublished } from '~/jobiqo/components/Job/Detail/utils';
import { isAnonymousByHeaders } from '~/jobiqo/lib/auth';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
import { getAppConfig, getServerConfigByName } from '~/jobiqo/lib/config';

interface JobPageProps {
  path: string;
  /**
   * If the user is authenticated.
   */
  authenticated: boolean;
  /**
   * The user roles.
   */
  user: User;
  /**
   * The apollo client.
   */
  apolloClient: any;
  data: any;
  loading: boolean;
  error: ApolloError | undefined;
  noFollowExternalLinks: boolean;
  plainTextExternalLinks: boolean;
}

class JobPage extends Component<JobPageProps> {
  public render() {
    const {
      authenticated,
      user,
      data,
      loading,
      error,
      noFollowExternalLinks,
      plainTextExternalLinks,
      ...props
    } = this.props;
    const { content }: { content: Entity } = data.jobByPath;

    return (
      <GdprConsentProvider>
        <JobAlertProvider>
          <JobPageDetail
            {...{
              job: content as Job,
              loading,
              error,
              authenticated,
              noFollowExternalLinks,
              plainTextExternalLinks,
              user,
              ...props,
            }}
          />
        </JobAlertProvider>
      </GdprConsentProvider>
    );
  }
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const backendConfig = await getAppConfig(ctx);
  const SITE_CAMPAIGN_ENABLED: boolean = !!getServerConfigByName(
    backendConfig,
    'site_campaign_enabled',
  )?.value[0];
  const apolloClient = initApollo({}, ctx);
  const frontendConfig = getServerFrontendConfig();
  // Because jobs are nodes we need to send Drupal the actual URL (translated).
  // ctx.resolvedUrl is the original URL.
  const path = getCleanUrlPart(getTranslatedLink(ctx.resolvedUrl, ctx.locale));
  const remainingQuerystring = ctx.resolvedUrl.includes('?')
    ? ctx.resolvedUrl.split('?')[1]
    : '';

  const { loading, error, data } = await apolloClient.query({
    query: getJobByPath(),
    variables: {
      path,
      language: ctx.locale,
    },
  });

  // Run all common queries like metatags and menus.
  await commonQueries(ctx, apolloClient, path, {
    siteCampaignEnabled: SITE_CAMPAIGN_ENABLED,
  });

  if (error) {
    return {
      props: {
        error: true,
      },
    };
  }

  if (data?.jobByPath?.code === 403) {
    return {
      props: {},
      redirect: {
        destination: '/403',
      },
    };
  }

  if (path !== '/' && data?.jobByPath?.code === 404) {
    return {
      notFound: true,
    };
  }

  if (
    isRedirectCode(data?.jobByPath?.code) &&
    path !== data?.jobByPath?.target
  ) {
    // If we get a redirect response redirect to the target.
    return {
      redirect: {
        destination: `${data?.jobByPath?.target}${
          remainingQuerystring ? `?${remainingQuerystring}` : ''
        }`,
        statusCode: data.jobByPath.code,
      },
      props: {},
    };
  }

  // Redirect to the external application link if the auto_apply_redirect param is present in the URL.
  if (ctx.query?.auto_apply_redirect) {
    if (data?.jobByPath?.content?.applicationLink) {
      return {
        redirect: {
          destination: data?.jobByPath?.content?.applicationLink,
          permanent: true,
        },
      };
    }

    // If the job doesn't have an external apply link redirect to the internal apply page instead.
    return {
      redirect: {
        destination: gsspRedirect(
          ctx,
          `/job/apply/` + data?.jobByPath?.content?.id,
        ),
        permanent: false,
      },
      props: {},
    };
  }

  // If the job is unpublished set to 404.
  const isJobPublished = getIsJobPublished(data?.jobByPath?.content);
  if (!isJobPublished) {
    ctx.res.statusCode = 404;
  }

  if (isAnonymousByHeaders(ctx.req)) {
    ctx.res.setHeader(
      'Cache-Control',
      'public, s-maxage=1800, stale-while-revalidate',
    );
  }

  return addApolloState(apolloClient, {
    props: {
      path,
      data,
      loading,
      error: error ?? false,
      noFollowExternalLinks:
        frontendConfig.JOBS.DETAIL_NOFOLLOW_EXTERNAL_DESCRIPTION_LINKS ?? false,
      plainTextExternalLinks:
        frontendConfig.JOBS.DETAIL_PLAINTEXT_EXTERNAL_DESCRIPTION_LINKS ??
        false,
    },
  });
};

export default JobPage;
